import { useMemo } from 'react';
import { GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { AdmissionMethodSchema, AdmissionMethodYearStatsSchema } from '../../../services/admissionMethodApi';
import { z } from 'zod';
import { useAppSelector } from '../../../store/hooks';
import { MOBILE_CELL_STYLES, MOBILE_GUIDE } from '../../../utils/responsive';

type AdmissionMethod = z.infer<typeof AdmissionMethodSchema>;

interface UseAdmissionMethodsColumnsProps {
	admissionMethodStats?: { stats: z.infer<typeof AdmissionMethodYearStatsSchema>[] };
	isAdmin: boolean;
	isMobile?: boolean;
	onEdit: (admissionMethod: AdmissionMethod) => () => void;
	onDelete: (admissionMethod: AdmissionMethod) => () => void;
}

export function useAdmissionMethodsColumns({ isAdmin, admissionMethodStats, isMobile = false, onEdit, onDelete }: UseAdmissionMethodsColumnsProps) {
	const years = useAppSelector((state) => state.filters.years);

	return useMemo<GridColDef[]>(
		() => {
			// Mobile: condensed single column with code, name, and latest year
			if (isMobile) {
				const sortedYears = [...years].sort((a: number, b: number) => a - b);
				const latestYear = sortedYears[sortedYears.length - 1];
				const admissionMethodYearDataMap = new Map<string, Record<string, number>>();

				if (admissionMethodStats?.stats) {
					admissionMethodStats.stats.forEach((stat) => {
						admissionMethodYearDataMap.set(stat.maPhuongThuc, stat.yearData);
					});
				}

				return [
					{
						field: 'tenPhuongThuc',
						headerName: 'Phương thức',
						minWidth: MOBILE_CELL_STYLES.MIN_WIDTH,
						flex: 1,
						sortable: true,
						filterable: false,
						renderCell: ({ row }) => {
							const yearData = admissionMethodYearDataMap.get(row.maPhuongThuc);
							const latestCount = latestYear && yearData ? yearData[latestYear.toString()] || 0 : 0;
							return (
								<Box
									sx={{
										width: '100%',
										height: '100%',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'flex-start',
										justifyContent: 'center',
										padding: MOBILE_CELL_STYLES.PADDING,
										whiteSpace: 'normal',
										wordWrap: 'break-word',
										lineHeight: MOBILE_CELL_STYLES.LINE_HEIGHT,
										gap: MOBILE_CELL_STYLES.GAP,
									}}
								>
									<Typography variant="caption" color="text.secondary">
										{row.maPhuongThuc || 'N/A'}
									</Typography>
									<Typography variant="body2" fontWeight="bold">
										{row.tenPhuongThuc || 'Không có tên'}
									</Typography>
									{latestYear && (
										<Typography variant="caption" color="text.secondary">
											{latestYear}: {latestCount} sinh viên
										</Typography>
									)}
									<Typography variant="caption" color={MOBILE_GUIDE.COLOR} fontWeight={MOBILE_GUIDE.FONT_WEIGHT}>
										{MOBILE_GUIDE.TEXT}
									</Typography>
								</Box>
							);
						},
					},
				];
			}

			const baseColumns: GridColDef[] = [
				{
					field: 'stt',
					headerName: 'STT',
					width: 60,
					headerAlign: 'center',
					align: 'center',
					sortable: false,
					filterable: false,
					renderCell: ({ api, id }) => {
						const currentPage = api.state.pagination.paginationModel.page;
						const pageSize = api.state.pagination.paginationModel.pageSize;
						const allRows = api.getRowModels();
						const rowIndex = Array.from(allRows.keys()).indexOf(id as string);
						const stt = currentPage * pageSize + rowIndex + 1;
						return stt;
					},
				},
				{
					field: 'maPhuongThuc',
					headerName: 'Mã phương thức',
					width: 150,
					headerAlign: 'center',
					align: 'center',
					sortable: true,
					filterable: false
				},
				{
					field: 'tenPhuongThuc',
					headerName: 'Tên phương thức',
					width: 300,
					headerAlign: 'center',
					sortable: true,
					filterable: false,
					renderCell: ({ value }) => value || 'Không có tên',
				},
			];

			// Add dynamic year columns based on real data
			const yearColumns: GridColDef[] = [];
			if (years.length > 0 && admissionMethodStats?.stats) {
				const sortedYears = [...years].sort((a: number, b: number) => a - b);

				const admissionMethodYearDataMap = new Map<string, Record<string, number>>();

				admissionMethodStats.stats.forEach((stat) => {
					admissionMethodYearDataMap.set(stat.maPhuongThuc, stat.yearData);
				});

				sortedYears.slice(-5).forEach((year: number) => {
					yearColumns.push({
						field: year.toString(),
						headerName: year.toString(),
						width: 80,
						type: 'number',
						headerAlign: 'center',
						align: 'center',
						sortable: true,
						filterable: false,
						renderCell: ({ row }: { row: AdmissionMethod }) => {
							const yearData = admissionMethodYearDataMap.get(row.maPhuongThuc);
							const count = yearData?.[year.toString()] || 0;
							return count;
						},
					});
				});
			}

			const actionColumns: GridColDef[] = isAdmin ? [{
				field: 'actions' as const,
				type: 'actions' as const,
				headerName: 'Thao tác',
				width: 120,
				headerAlign: 'center' as const,
				getActions: ({ row }: { row: AdmissionMethod }) => [
					<GridActionsCellItem
						key="edit-item"
						icon={<EditIcon />}
						label="Chỉnh sửa"
						onClick={onEdit(row)}
						color="primary"
					/>,
					<GridActionsCellItem
						key="delete-item"
						icon={<DeleteIcon />}
						label="Xóa"
						onClick={onDelete(row)}
						color="error"
					/>,
				],
			}] : [];

			return [...baseColumns, ...yearColumns, ...actionColumns];
			},
		[isAdmin, years, admissionMethodStats, isMobile, onEdit, onDelete],
	);
}

export default useAdmissionMethodsColumns;